import { useMemo, useRef } from 'react'
import dynamic from 'next/dynamic'
import Banner from '@components/Banner'
import CallToAction from '@components/CallToAction'
import Layout from '@components/CompoundLayout'
import {
  DescriptionWrapper,
  StyledButtons,
  StyledContent,
  StyledTitle,
  StyledVideoPlayer,
  StyledWrapper,
} from '@components/CompoundLayout/styles'
import Headers from '@components/Headers'
import LogoFooter from '@components/Layout/styles/Logo'
import LogoLink from '@components/Layout/styles/LogoLink'
import VideoMessageSign from '@components/Layout/styles/VideoMessageSign'
import NavBar from '@components/NavBar'
import RenderIf from '@components/RenderIf'
import RenderIfOwner from '@components/RenderIfOwner'
import ShowMoreTextContainer from '@components/ShowMoreTextContainer'
import SpinnerIcon from '@components/VideoPlayer/components/Scene/styles/SpinnerIcon'
import { environmentsConfig } from '@config'
import { CALENDAR_CUSTOM_LAYOUTS, MUX_STATUS } from '@constants/constants'
import { useSharePageHandlers } from '@hooks/lib/useSharePageHandlers'
import { CampaignMetaLayout, DynamicVariables, PlayerAnimation } from '@interfaces'
import { Campaign as SharedCampaign } from '@sendspark/player/dist/types'
import { VideoPlayerEvents } from '@sendspark/player/dist/VideoPlayer'
import SidePanel from '@components/SidePanel'
import DefaultIntroAnimation from '@styles/views/intro-animation'
import { EnvelopeIntroAnimation } from '@styles/views/intro-animation'
import VideoPreview from '../shared/VideoPreview'
import { BaseThemeProps } from '../interfaces'

const Player = dynamic(() => import('@components/Player'), { ssr: false })

export interface V2ThemeProps extends BaseThemeProps {
  dynamicVariables: DynamicVariables
}

const V2Theme: React.FC<V2ThemeProps> = ({
  campaign,
  env,
  pageTitle,
  isLinkedInRequest,
  isOwnerVideo,
  legacyAnalytics,
  playerEvents,
  dynamicVariables,
  isEditPage,
  replayVideo,
  setReplayVideo,
  title,
  description,
  showCta,
  callToAction,
  isMultipleCTAsSupported,
  trackCTAClick,
  trackViralLink,
  viralLink,
  playerEventsRef: hostPlayerEventsRef,
  slots,
}) => {
  const { handleCopyLink, handleDownload } = useSharePageHandlers(campaign)
  
  const { style } = campaign
  const isEnvelopeAnimation = campaign.theme?.properties?.player?.animation === PlayerAnimation.ENVELOPE
  const IntroAnimationContainer = isEnvelopeAnimation ? EnvelopeIntroAnimation : DefaultIntroAnimation
  const hasCalendar = !!campaign?.calendar?.provider && !!campaign?.calendar?.link
  const hasReadyVideo = campaign?.meta?.video?.muxStatus === MUX_STATUS.READY
  const hasTranscript = !!campaign?.transcriptionsViewEnabled && hasReadyVideo

  // Shared with the transcript panel so line clicks can call the controller's seekTo, and with
  // the page's CTA tracking so a click reaches Insight (SK-567). Prefer the host's ref when it
  // provides one — falling back keeps the theme usable on its own (previews).
  const localPlayerEventsRef = useRef<VideoPlayerEvents | null>(null)
  const playerEventsRef = hostPlayerEventsRef ?? localPlayerEventsRef

  const calendarCustomStyles = hasCalendar ? CALENDAR_CUSTOM_LAYOUTS[campaign?.calendar?.provider as string] : false

  const CustomLayout = useMemo(() => {
    if (!calendarCustomStyles) return Layout;

    const LayoutObject = { ...Layout };

    Object.entries(calendarCustomStyles).forEach(([key, value]) => {
      if (Layout[value]) {
        LayoutObject[key] = Layout[value];
      }
    });

    return LayoutObject;
  }, [calendarCustomStyles]);

  const DefaultNavBar = (
    <NavBar
      background={style?.background}
      border={{ color: style?.borderColor }}
      icon={{ color: style?.iconColor }}
      url={campaign.meta.logo?.url}
      handleOnDownload={handleDownload}
      handleOnCopyLink={handleCopyLink}
      isNCSU={campaign?.featureFlips?.ncsuProject}
      navbarThemeStyle={campaign.theme?.properties?.style?.navbar}
    />
  )

  const DefaultMain = (
    <>
    <StyledContent animated={isEnvelopeAnimation}>
      {/* Header Layout 2 */}
      <RenderIf condition={!!title && campaign?.meta?.layout === CampaignMetaLayout.TITLE_ABOVE}>
        <StyledTitle color={style.heading1} style={{ justifyContent: 'center' }}>{title}</StyledTitle>
      </RenderIf>
    </StyledContent>
    <CustomLayout.Left>
      <StyledWrapper>
        <IntroAnimationContainer
          name={dynamicVariables?.firstname || campaign?.dynamicVideoProps?.contact?.name || 'Watch Video'}
          styles={campaign.theme?.properties?.style}
          logoUrl={campaign.meta.logo?.url}
        >
          <StyledVideoPlayer>
            <RenderIf
              condition={
                campaign?.meta?.video?.muxStatus === MUX_STATUS.READY ||
                (campaign?.meta?.video?.uploadingStatus === 'pending' &&
                  campaign?.meta?.video?.muxStatus === MUX_STATUS.PREPARING)
              }
            >
              <Player
                isEditPage={isEditPage}
                campaign={(campaign as unknown) as SharedCampaign}
                apiV2Url={env.apiV2Url}
                beaconUrl={env.beaconUrl}
                legacyAnalytics={legacyAnalytics}
                beaconDebug={env.beaconDebug}
                insightUrl={
                  env.insightCaptureEnabled && !isOwnerVideo && env.insightUrl
                    ? env.insightUrl
                    : undefined
                }
                insightApiKey={
                  env.insightCaptureEnabled && !isOwnerVideo && env.insightUrl
                    ? env.insightApiKey || undefined
                    : undefined
                }
                events={isOwnerVideo ? (null as any) : playerEvents}
                apiV1Url={environmentsConfig.apiV1Url}
                nodeEnv={environmentsConfig.nodeEnv}
                autoPlay={
                  campaign?.featureFlips?.autoPlay && campaign?.autoPlay
                }
                thumbsUp={
                  campaign?.featureFlips?.thumbsUp && campaign?.thumbsUp
                }
                gapCombinedVideoPlayer={
                  campaign?.featureFlips?.gapCombinedVideoPlayer &&
                  campaign?.gapCombinedVideoPlayer
                }
                replayVideo={replayVideo}
                setReplayVideo={setReplayVideo}
                playerEventsRef={playerEventsRef}
              />
            </RenderIf>
            <RenderIf
              condition={
                ['uploading', 'completed'].includes(
                  campaign?.meta?.video?.uploadingStatus || ''
                ) &&
                campaign?.meta?.video?.muxStatus === MUX_STATUS.PREPARING
              }
            >
              <VideoPreview>
                <span>
                  <div>
                    <SpinnerIcon spinnerBackgroundColor="#00000000" />
                  </div>
                  <span>Processing...</span>
                </span>
                <img src={env?.videoProcessingGIF} alt="" />
              </VideoPreview>
            </RenderIf>
          </StyledVideoPlayer>
        </IntroAnimationContainer>
        <StyledContent animated={isEnvelopeAnimation}>
          {/* Header Layout 1 */}
          <RenderIf condition={!!title && campaign?.meta?.layout === CampaignMetaLayout.DEFAULT}>
            <StyledTitle color={style.heading1}>{title}</StyledTitle>
          </RenderIf>
          {/* Description */}
          <RenderIf condition={!!description}>
            <DescriptionWrapper>
              <ShowMoreTextContainer
                color={style.heading2}
                text={description}
                maxLength={180}
                showMoreColor={style.responseButtonBackground}
              />
            </DescriptionWrapper>
          </RenderIf>
          {/* Call-to-actions */}
          <RenderIf condition={showCta}>
            <StyledButtons>
              <CallToAction
                isV2Theme
                styles={style}
                message={callToAction}
                trackCTAClick={trackCTAClick}
                isMultipleCTAsSupported={isMultipleCTAsSupported}
              />
            </StyledButtons>
          </RenderIf>
        </StyledContent>
      </StyledWrapper>
    </CustomLayout.Left>
    <RenderIf condition={hasCalendar || hasTranscript}>
      <CustomLayout.Right>
        <SidePanel
          campaign={campaign}
          playerEventsRef={playerEventsRef}
          hasCalendar={hasCalendar}
          hasTranscript={hasTranscript}
        />
      </CustomLayout.Right>
    </RenderIf>
    </>
  )
  
  const DefaultFooter = (
    <RenderIf condition={!!campaign?.sendsparkBrandingEnabled}>
      <CustomLayout.Footer>
        <VideoMessageSign>
          Video Message by
          <LogoLink
            onClick={trackViralLink}
            className="logo-link"
            href={viralLink}
            target="_blank"
            rel="noreferrer"
          >
            <LogoFooter />
          </LogoLink>
        </VideoMessageSign>
      </CustomLayout.Footer>
    </RenderIf>
  )

  return (
    <Layout
      backgroundImage={campaign.theme?.properties?.style?.backgroundImage}
      backgroundGradient={campaign.theme?.properties?.style?.backgroundGradient}
    >
      <RenderIfOwner
        cryptoKey={env.cryptoKey || ''}
        isOwnerVideo={isOwnerVideo}
      >
        <Banner
          text="This is a preview. Your view will not show in analytics."
          textButton="Edit Video"
          link={`${env.editPageUrl}${campaign?.id}`}
        />
      </RenderIfOwner>
      <Headers
        title={pageTitle}
        campaign={campaign}
        sendsparkBrandingEnabled={campaign.sendsparkBrandingEnabled}
        isLinkedInRequest={isLinkedInRequest}
      />
  
      <CustomLayout.Top>
        {slots?.NavBar || DefaultNavBar}
      </CustomLayout.Top>

      {slots?.PreMain}

      <CustomLayout.Main>
        {slots?.Main || DefaultMain}
      </CustomLayout.Main>

      {slots?.PostMain}

      {slots?.Footer || DefaultFooter}
      
    </Layout>
  )
}

export default V2Theme